import { Component, OnInit, ChangeDetectionStrategy, Input, OnChanges, ViewChild, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { MeteringFilter, UserSettings, MeteringPointStub, MeteringPoint, MeteringSubFilter, UserInfo } from '@model';
import { MeteringFilterConfigFormComponent } from '@core/components/metering-filter-config-form/metering-filter-config-form.component';
import { SxDialogService } from '../../../services/sx-dialog.service';
import { MeteringType, MeteringFilterType } from '@enums';
import * as fromAccountStore from '@appStore/account';
import * as fromAnalyzeStore from '@appStore/analyze';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatExpansionPanel } from '@angular/material/expansion';
import { SubSink } from 'subsink';

@Component({
    selector: 'app-logic-metering-filter',
    templateUrl: './logic-metering-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LogicMeteringFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  meteringType: MeteringType;
  @Input()
  userSettings: UserSettings;
  @Input()
  subfilterEnabled = false;
  @Input()
  meteringPoints: MeteringPoint[];
  @Input()
  userInfo: UserInfo;
  @Input()
  filterArea: string;

  meteringPointsCount = 0;
  meteringPointsCountLabel = '';

  @ViewChild('subfilterPanel')
  subfilterPanel: MatExpansionPanel;

  subfilterOpenState = false;

  panelOpenState = false;
  filterUpdateWatch: Subject<MeteringSubFilter>;
  filterSettings: MeteringFilter;
  filterSettingsTypeEnum = MeteringFilterType;
  meteringTypeEnum = MeteringType;

  private subs = new SubSink();

  constructor(
    private sxDialogService: SxDialogService,
    private accountStore: Store<fromAccountStore.AccountState>,
    private analyzeStore: Store<fromAnalyzeStore.AnalyzeState>
  ) {}

  ngOnInit() {
    this.initFromSettings();

    this.filterUpdateWatch = new Subject<MeteringSubFilter>();
    this.subs.sink = this.filterUpdateWatch
      .pipe(
        debounceTime(750),
        distinctUntilChanged()
      )
      .subscribe(subfilter => this.accountStore.dispatch(new fromAccountStore.SetSubFilter(subfilter)));
  }

  ngOnChanges() {
    this.initFromSettings();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getMeteringFilter = (meteringType: MeteringType): MeteringFilter => {
    switch (meteringType) {
      case MeteringType.HotLogical:
        return this.userSettings.hotLogicalMeteringFilter;
      case MeteringType.ColdLogical:
        return this.userSettings.coldLogicalMeteringFilter;
    }
  };

  getMeteringPoints = (meteringType: MeteringType): MeteringPoint[] => {
    if (!this.meteringPoints || this.meteringPoints.length === 0) {
      return [];
    }
    switch (meteringType) {
      case MeteringType.HotLogical:
        return this.meteringPoints.filter(mp => mp.meteringType === MeteringType.Hot && mp.type === 'LOG');
      case MeteringType.ColdLogical:
        return this.meteringPoints.filter(mp => mp.meteringType === MeteringType.Cold && mp.type === 'LOG');
    }
  };

  initFromSettings() {
    let logicalMeteringPoints = this.getMeteringPoints(this.meteringType);
    this.meteringPointsCount = logicalMeteringPoints.length;

    if (this.userSettings) {
      this.filterSettings = this.getMeteringFilter(this.meteringType);
      let firstMeteringPoint = logicalMeteringPoints[0];
      let firstMeteringPointContained = firstMeteringPoint.containedMeteringPoints;

      if (
        this.filterSettings &&
        (this.filterSettings.filterType === MeteringFilterType.Unfiltered ||
          this.filterSettings.filterType === MeteringFilterType.MeteringPoint) &&
        (!this.filterSettings.filterId || this.filterSettings.filterId < 0) &&
        this.meteringPointsCount > 0
      ) {
        let includedMps = firstMeteringPointContained.map(
          mp => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true }
        );

        this.filterSettings = {
          ...this.filterSettings,
          filterId: firstMeteringPoint.id,
          filterType: MeteringFilterType.MeteringPoint,
          name: firstMeteringPoint.name,
          containsMeteringPointIds: firstMeteringPoint.containedMeteringPoints
            ? firstMeteringPoint.containedMeteringPoints.map(x => x.id)
            : [],
          subfilter: {
            ...this.filterSettings.subfilter,
            includedMeteringPoints: includedMps,
            excludedMeteringPoints: null
          }
        };
        this.accountStore.dispatch(
          new fromAccountStore.FilterSettingUpdate({
            meteringType: this.meteringType,
            filter: this.filterSettings,
            userId: this.userInfo.id,
            filterArea: this.filterArea,
            meteringPoints: this.meteringPoints
          })
        );
        return;
      }

      if (
        this.filterSettings &&
        this.filterSettings.filterType === MeteringFilterType.MeteringPoint &&
        this.filterSettings.filterId &&
        this.filterSettings.filterId > 0
      ) {
        firstMeteringPoint = logicalMeteringPoints.find(x => x.id === this.filterSettings.filterId);
        if (firstMeteringPoint) {
          firstMeteringPointContained = firstMeteringPoint.containedMeteringPoints;
        } else {
          firstMeteringPointContained = [];
        }
      }

      if (
        this.filterSettings &&
        this.filterSettings.subfilter &&
        !this.filterSettings.subfilter.excludedMeteringPoints
      ) {
        let exludedIds = this.filterSettings.subfilter.includedMeteringPoints
          ? (this.filterSettings.filterType === MeteringFilterType.Unfiltered
              ? firstMeteringPointContained.filter(mp => mp.meteringType === this.meteringType).map(mp => mp.id)
              : this.filterSettings.containsMeteringPointIds.map(mp => mp)
            ).filter(mpid => !this.filterSettings.subfilter.includedMeteringPoints.find(imp => imp.id === mpid))
          : null;

        this.filterSettings = {
          ...this.filterSettings,
          filterId: firstMeteringPoint.id,
          subfilter: {
            ...this.filterSettings.subfilter,
            includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
              ? firstMeteringPointContained
                  .filter(mp => this.filterSettings.subfilter.includedMeteringPoints.find(imp => imp.id === mp.id))
                  .map(mp => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true })
              : null,
            excludedMeteringPoints: firstMeteringPointContained
              .filter(mp => exludedIds.find(exl => exl === mp.id))
              .map(mp => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: false })
          }
        };
      }
    }

    this.updateSubFilterLabel();
  }

  toggleSelected = (mps: MeteringPointStub) => {
    if (mps.selected && this.isLastSelected(mps)) {
      return false;
    }

    mps = { ...mps, selected: !mps.selected };

    this.filterSettings = {
      ...this.filterSettings,
      subfilter: {
        ...this.filterSettings.subfilter,
        includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.includedMeteringPoints.map(mp => (mp.id === mps.id ? mps : mp))
          : null,
        excludedMeteringPoints: this.filterSettings.subfilter.excludedMeteringPoints
          ? this.filterSettings.subfilter.excludedMeteringPoints.map(mp => (mp.id === mps.id ? mps : mp))
          : null
      } as MeteringSubFilter
    };
    this.triggerSubFilterUpdate(this.filterSettings.subfilter);
  };

  isLastSelected = (mps: MeteringPointStub) => {
    let selectedExcluded = this.filterSettings.subfilter.excludedMeteringPoints
      ? this.filterSettings.subfilter.excludedMeteringPoints.filter(mp => mp.selected)
      : [];
    let selectedIncluded = this.filterSettings.subfilter.includedMeteringPoints
      ? this.filterSettings.subfilter.includedMeteringPoints.filter(mp => mp.selected)
      : [];
    return selectedExcluded.length + selectedIncluded.length === 1;
  };

  configureFilter = () => {
    let dialogRef = this.sxDialogService.openDialog(MeteringFilterConfigFormComponent, {
      meteringType: this.meteringType,
      filterSettings: this.filterSettings,
      filterArea: this.filterArea
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result: string) => {
      if (result === 'save' && this.subfilterEnabled && this.subfilterPanel && this.subfilterPanel.expanded) {
        this.subfilterPanel.close();
      }
    });
  };

  updateSubFilterLabel = () => {
    if (this.filterSettings.subfilter && this.subfilterEnabled) {
      this.meteringPointsCount = 0;
      let meteringPointsSelectedCount = 0;

      if (this.filterSettings.subfilter.includedMeteringPoints) {
        this.meteringPointsCount =
          this.meteringPointsCount + this.filterSettings.subfilter.includedMeteringPoints.length;
      }

      if (this.filterSettings.subfilter.excludedMeteringPoints) {
        this.meteringPointsCount =
          this.meteringPointsCount + this.filterSettings.subfilter.excludedMeteringPoints.length;
      }
      if (this.filterSettings.subfilter.includedMeteringPoints) {
        meteringPointsSelectedCount =
          meteringPointsSelectedCount +
          this.filterSettings.subfilter.includedMeteringPoints.filter(i => i.selected).length;
      }

      if (this.filterSettings.subfilter.excludedMeteringPoints) {
        meteringPointsSelectedCount =
          meteringPointsSelectedCount +
          this.filterSettings.subfilter.excludedMeteringPoints.filter(i => i.selected).length;
      }

      this.meteringPointsCountLabel = meteringPointsSelectedCount + '/' + this.meteringPointsCount;
      return;
    }

    if (!this.filterSettings || this.filterSettings.filterType === MeteringFilterType.Unfiltered) {
      this.meteringPointsCountLabel = this.meteringPointsCount + '';
      return;
    }

    if (this.filterSettings.containsMeteringPointIds.length === 1) {
      this.meteringPointsCountLabel = '';
      return;
    }

    this.meteringPointsCountLabel = this.filterSettings.subtitle;
  };

  resetSubfilter = () => {
    this.filterSettings = {
      ...this.filterSettings,
      subfilter: {
        ...this.filterSettings.subfilter,
        excludedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.excludedMeteringPoints.map(
              mp => <MeteringPointStub>{ ...mp, selected: false }
            )
          : this.filterSettings.subfilter.excludedMeteringPoints.map(
              mp => <MeteringPointStub>{ ...mp, selected: true }
            ),
        includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.includedMeteringPoints.map(mp => <MeteringPointStub>{ ...mp, selected: true })
          : this.filterSettings.subfilter.includedMeteringPoints
      }
    };

    this.triggerSubFilterUpdate(this.filterSettings.subfilter);
    this.updateSubFilterLabel();
  };

  toggleAllInSubfilter = (state: boolean) => {
    this.filterSettings = {
      ...this.filterSettings,
      subfilter: {
        ...this.filterSettings.subfilter,
        excludedMeteringPoints:
          !this.filterSettings.subfilter.includedMeteringPoints || this.panelOpenState === true
            ? this.filterSettings.subfilter.excludedMeteringPoints.map(
                mp => <MeteringPointStub>{ ...mp, selected: state }
              )
            : this.filterSettings.subfilter.excludedMeteringPoints,
        includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.includedMeteringPoints.map(
              mp => <MeteringPointStub>{ ...mp, selected: state }
            )
          : this.filterSettings.subfilter.includedMeteringPoints
      }
    };

    this.triggerSubFilterUpdate(this.filterSettings.subfilter);
    this.updateSubFilterLabel();

    if (!state) {
      // Återställer Graf och Tabell när alla anläggningar avmarkeras
      this.analyzeStore.dispatch(new fromAnalyzeStore.AnalyzeResetGraphValues());
    }
  };

  triggerSubFilterUpdate = (subfilter: MeteringSubFilter) => {
    this.filterUpdateWatch.next(subfilter);
  };

  toggleSubFilter = () => {
    if (!this.filterSettings.subfilter) {
      this.filterSettings = {
        ...this.filterSettings,
        subfilter: <MeteringSubFilter>{
          meteringType: this.filterSettings.meteringType,
          excludedMeteringPoints: (this.filterSettings.filterType !== MeteringFilterType.Unfiltered
            ? this.meteringPoints.filter(mp =>
                this.filterSettings.containsMeteringPointIds.find(mpid => mpid === mp.id)
              )
            : this.meteringPoints.filter(mp => mp.meteringType === this.meteringType)
          ).map(mp => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true })
        }
      };
      this.triggerSubFilterUpdate(this.filterSettings.subfilter);
    }
    this.subfilterPanel.toggle();
    this.subfilterOpenState = this.subfilterPanel.expanded;
  };
}
